// src/Dashboard/pages/user/Inscription/MemberCardPage.jsx

import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  FaArrowLeft,
  FaBirthdayCake,
  FaCheck,
  FaDownload,
  FaIdCard,
  FaPrint,
  FaShieldAlt,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { toast } from "react-toastify";

import useScrollToTop from "../../../../hooks/useScollToTop";

import {
  formatShortDateFR,
  getAdherentAvatar,
  isLicenceValidated,
  isReglementValidated,
  toAbsoluteUrl,
} from "./utils/inscriptionUtils";

const PDF_CARD_WIDTH_MM = 110;

function escapeHtml(value = "") {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function buildExportCardHtml({ adherent, dossier, settings, activeSeason }) {
  const reglementValidated = isReglementValidated(dossier);
  const licenceValidated = isLicenceValidated(dossier);

  const clubLogo = toAbsoluteUrl(settings?.logo_club || "");
  const avatar = getAdherentAvatar(adherent);

  const clubName = settings?.nom_club || "Club sportif";

  const season =
    adherent?.saison_active || activeSeason || settings?.saison_active || "—";

  const nom = String(adherent?.nom || "—").toUpperCase();
  const prenom = adherent?.prenom || "—";
  const naissance = formatShortDateFR(adherent?.date_naissance);
  const licence = adherent?.licence_number || "—";

  const statusBadge = (checked, label) => `
    <div class="status-badge ${checked ? "status-ok" : "status-ko"}">
      <span class="status-icon">${checked ? "✓" : "×"}</span>
      <span>${escapeHtml(label)}</span>
    </div>
  `;

  return `
    <div class="export-card">
      <div class="bg-gradient"></div>
      <div class="shape-black"></div>
      <div class="shape-gold"></div>
      <div class="circle-deco"></div>
      <div class="left-deco"></div>

      <div class="card-grid">
        <div class="left-col">
          <div class="photo-box">
            <img src="${escapeHtml(avatar)}" crossorigin="anonymous" />
          </div>

          <div class="season-box">
            Saison <span>${escapeHtml(season)}</span>
          </div>

          <div class="status-list">
            ${statusBadge(reglementValidated, "Règlement ok")}
            ${statusBadge(licenceValidated, "Licence prise")}
          </div>
        </div>

        <div class="right-col">
          <div class="top-row">
            <div class="title-block">
              <div class="small-title">Carte de membre</div>
              <div class="gold-line"></div>
              <div class="club-name">${escapeHtml(clubName)}</div>
            </div>

            <div class="logo-box">
              ${
                clubLogo
                  ? `<img src="${escapeHtml(clubLogo)}" crossorigin="anonymous" />`
                  : `<div class="logo-placeholder">Club</div>`
              }
            </div>
          </div>

          <div class="info-box">
            <div class="info-row">
              <div class="info-label">Nom :</div>
              <div class="info-value uppercase">${escapeHtml(nom)}</div>
            </div>

            <div class="info-row">
              <div class="info-label">Prénom :</div>
              <div class="info-value">${escapeHtml(prenom)}</div>
            </div>

            <div class="info-row">
              <div class="info-label">Naissance :</div>
              <div class="info-value">${escapeHtml(naissance)}</div>
            </div>

            <div class="info-row last">
              <div class="info-label">Licence :</div>
              <div class="licence-value">${escapeHtml(licence)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function getExportCardCss() {
  return `
    .export-card,
    .export-card * {
      box-sizing: border-box;
      color-scheme: light;
    }

    .export-card {
      position: relative;
      width: 920px;
      height: 580px;
      overflow: hidden;
      border-radius: 34px;
      background: #ffffff;
      color: #020617;
      font-family: Arial, Helvetica, sans-serif;
      box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
      border: 1px solid #e2e8f0;
    }

    .bg-gradient {
      position: absolute;
      inset: 0;
      background:
        radial-gradient(circle at top right, rgba(234,179,8,0.16), transparent 30%),
        linear-gradient(135deg, #ffffff 0%, #f8fafc 48%, #ffffff 100%);
    }

    .shape-black {
      position: absolute;
      left: -48px;
      top: 0;
      width: 360px;
      height: 144px;
      background: #020617;
      transform: skewX(-32deg);
    }

    .shape-gold {
      position: absolute;
      left: 112px;
      top: 0;
      width: 48px;
      height: 144px;
      background: #eab308;
      transform: skewX(-32deg);
    }

    .circle-deco {
      position: absolute;
      right: 16px;
      top: 96px;
      width: 256px;
      height: 256px;
      border-radius: 999px;
      border: 22px solid #f1f5f9;
    }

    .left-deco {
      position: absolute;
      left: 0;
      top: 176px;
      width: 80px;
      height: 288px;
      border-radius: 0 999px 999px 0;
      background: #f1f5f9;
    }

    .card-grid {
      position: relative;
      z-index: 10;
      display: grid;
      grid-template-columns: 340px 1fr;
      gap: 24px;
      height: 100%;
      padding: 28px;
    }

    .left-col {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .photo-box {
      width: 100%;
      height: 330px;
      overflow: hidden;
      border-radius: 28px;
      border: 4px solid #eab308;
      background: #f1f5f9;
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
    }

    .photo-box img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .season-box {
      margin-top: 16px;
      border-radius: 22px;
      background: #020617;
      color: #ffffff;
      padding: 13px 20px;
      text-align: center;
      font-size: 18px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.14em;
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
    }

    .season-box span {
      color: #facc15;
    }

    .status-list {
      margin-top: 12px;
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .status-badge {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;
      border-radius: 16px;
      padding: 10px 12px;
      font-size: 13px;
      font-weight: 800;
      box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
      border: 1px solid #e2e8f0;
    }

    .status-ok {
      background: #ecfdf5;
      color: #047857;
      border-color: #a7f3d0;
    }

    .status-ko {
      background: #f1f5f9;
      color: #64748b;
      border-color: #cbd5e1;
    }

    .status-icon {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      border-radius: 999px;
      color: #ffffff;
      font-size: 20px;
      font-weight: 900;
      line-height: 1;
    }

    .status-ok .status-icon {
      background: #059669;
    }

    .status-ko .status-icon {
      background: #94a3b8;
    }

    .right-col {
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    .top-row {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
    }

    .title-block {
      min-width: 0;
      padding-top: 16px;
    }

    .small-title {
      font-size: 30px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: #020617;
    }

    .gold-line {
      margin-top: 12px;
      width: 288px;
      height: 4px;
      border-radius: 999px;
      background: #eab308;
    }

    .club-name {
      margin-top: 24px;
      max-width: 520px;
      font-size: 48px;
      line-height: 1.08;
      font-weight: 900;
      letter-spacing: -0.04em;
      color: #020617;
    }

    .logo-box {
      flex: 0 0 auto;
      width: 128px;
      height: 128px;
      border-radius: 999px;
      background: #ffffff;
      padding: 8px;
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
      border: 1px solid #e2e8f0;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .logo-box img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      display: block;
    }

    .logo-placeholder {
      color: #eab308;
      font-size: 24px;
      font-weight: 900;
    }

    .info-box {
      margin-top: 32px;
      border-radius: 24px;
      background: rgba(255,255,255,0.96);
      padding: 16px;
      box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
      border: 1px solid #f1f5f9;
    }

    .info-row {
      display: grid;
      grid-template-columns: 185px minmax(0, 1fr);
      align-items: center;
      border-bottom: 1px solid rgba(234,179,8,0.6);
      padding-bottom: 8px;
      margin-bottom: 12px;
    }

    .info-row.last {
      margin-bottom: 0;
    }

    .info-label {
      font-size: 20px;
      font-weight: 500;
      color: #334155;
    }

    .info-value {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 30px;
      font-weight: 700;
      color: #020617;
    }

    .uppercase {
      text-transform: uppercase;
    }

    .licence-value {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      border-radius: 12px;
      background: #ffffff;
      padding: 6px 12px;
      font-size: 23px;
      font-weight: 700;
      color: #020617;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
      border: 1px solid #e2e8f0;
    }
  `;
}

async function waitForImages(container) {
  const images = Array.from(container.querySelectorAll("img"));

  await Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }

          img.onload = resolve;
          img.onerror = resolve;
        }),
    ),
  );
}

async function buildMemberCardCanvasFromSafeHtml({
  adherent,
  dossier,
  settings,
  activeSeason,
}) {
  const wrapper = document.createElement("div");

  wrapper.style.position = "fixed";
  wrapper.style.left = "-10000px";
  wrapper.style.top = "0";
  wrapper.style.width = "920px";
  wrapper.style.height = "580px";
  wrapper.style.background = "#ffffff";
  wrapper.style.zIndex = "-1";

  const style = document.createElement("style");
  style.textContent = getExportCardCss();

  const html = document.createElement("div");
  html.innerHTML = buildExportCardHtml({
    adherent,
    dossier,
    settings,
    activeSeason,
  });

  wrapper.appendChild(style);
  wrapper.appendChild(html);

  document.body.appendChild(wrapper);

  try {
    await waitForImages(wrapper);

    const card = wrapper.querySelector(".export-card");

    const canvas = await html2canvas(card, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
      logging: false,
      windowWidth: 920,
      windowHeight: 580,
    });

    return canvas;
  } finally {
    document.body.removeChild(wrapper);
  }
}

function addCardCanvasToPdf(canvas) {
  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = pdf.internal.pageSize.getWidth();

  const imgWidth = PDF_CARD_WIDTH_MM;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  const x = (pageWidth - imgWidth) / 2;
  const y = 18;

  pdf.addImage(imgData, "PNG", x, y, imgWidth, imgHeight);

  return pdf;
}

function cleanFileName(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .toLowerCase();
}

function StatusBadge({ checked, label }) {
  return (
    <div
      className={`inline-flex w-full items-center gap-2 rounded-2xl px-3 py-2.5 text-xs font-bold shadow-sm ${
        checked
          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
          : "bg-slate-100 text-slate-500 ring-1 ring-slate-200"
      }`}
    >
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white ${
          checked ? "bg-emerald-600" : "bg-slate-400"
        }`}
      >
        {checked ? <FaCheck /> : <FaTimes />}
      </span>

      <span className="truncate">{label}</span>
    </div>
  );
}

function MemberCard({ adherent, dossier, settings, activeSeason, cardRef }) {
  const reglementValidated = isReglementValidated(dossier);
  const licenceValidated = isLicenceValidated(dossier);

  const clubLogo = toAbsoluteUrl(settings?.logo_club || "");
  const avatar = getAdherentAvatar(adherent);

  const clubName = settings?.nom_club || "Club sportif";

  const season =
    adherent?.saison_active || activeSeason || settings?.saison_active || "—";

  return (
    <div
      ref={cardRef}
      data-member-card-capture
      className="relative aspect-[85.6/54] w-[920px] overflow-hidden rounded-[34px] bg-white text-slate-950 shadow-2xl ring-1 ring-slate-200"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(234,179,8,0.16),transparent_30%),linear-gradient(135deg,#ffffff_0%,#f8fafc_48%,#ffffff_100%)]" />

      <div className="absolute -left-12 top-0 h-36 w-[360px] -skew-x-[32deg] bg-slate-950" />
      <div className="absolute left-28 top-0 h-36 w-12 -skew-x-[32deg] bg-yellow-500" />

      <div className="absolute right-4 top-24 h-64 w-64 rounded-full border-[22px] border-slate-100/90" />
      <div className="absolute left-0 top-44 h-72 w-20 rounded-r-full bg-slate-100/80" />

      <div className="relative z-10 grid h-full grid-cols-[340px_minmax(0,1fr)] gap-6 p-7">
        <div className="flex h-full flex-col">
          <div className="overflow-hidden rounded-[28px] border-4 border-yellow-500 bg-slate-100 shadow-lg">
            <img
              src={avatar}
              alt={`${adherent?.prenom || ""} ${adherent?.nom || ""}`}
              crossOrigin="anonymous"
              className="h-[330px] w-full object-cover"
            />
          </div>

          <div className="mt-4 rounded-[22px] bg-slate-950 px-5 py-3 text-center shadow-lg">
            <p className="text-lg font-bold uppercase tracking-[0.14em] text-white">
              Saison <span className="text-yellow-400">{season}</span>
            </p>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2">
            <StatusBadge checked={reglementValidated} label="Règlement ok" />
            <StatusBadge checked={licenceValidated} label="Licence prise" />
          </div>
        </div>

        <div className="flex min-w-0 flex-col">
          <div className="flex items-start justify-between gap-5">
            <div className="min-w-0 pt-4">
              <p className="text-3xl font-bold uppercase tracking-[0.08em] text-slate-950">
                Carte de membre
              </p>

              <div className="mt-3 h-1 w-72 rounded-full bg-yellow-500" />

              <div className="mt-6">
                <h1 className="max-w-[520px] text-5xl font-bold leading-tight tracking-tight text-slate-950">
                  {clubName}
                </h1>
              </div>
            </div>

            <div className="flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white p-2 shadow-lg ring-1 ring-slate-200">
              {clubLogo ? (
                <img
                  src={clubLogo}
                  alt={clubName}
                  crossOrigin="anonymous"
                  className="h-full w-full object-contain"
                />
              ) : (
                <FaShieldAlt className="text-6xl text-yellow-500" />
              )}
            </div>
          </div>

          <div className="mt-8 rounded-[24px] bg-white/95 p-4 shadow-sm ring-1 ring-slate-100">
            <div className="space-y-3">
              <div className="grid grid-cols-[185px_minmax(0,1fr)] items-center border-b border-yellow-500/60 pb-2">
                <p className="flex items-center gap-3 text-xl font-medium text-slate-700">
                  <FaUser className="text-slate-950" />
                  Nom :
                </p>

                <p className="truncate text-[1.85rem] font-semibold uppercase text-slate-950">
                  {adherent?.nom || "—"}
                </p>
              </div>

              <div className="grid grid-cols-[185px_minmax(0,1fr)] items-center border-b border-yellow-500/60 pb-2">
                <p className="flex items-center gap-3 text-xl font-medium text-slate-700">
                  <FaUser className="text-slate-950" />
                  Prénom :
                </p>

                <p className="truncate text-[1.85rem] font-semibold text-slate-950">
                  {adherent?.prenom || "—"}
                </p>
              </div>

              <div className="grid grid-cols-[185px_minmax(0,1fr)] items-center border-b border-yellow-500/60 pb-2">
                <p className="flex items-center gap-3 text-xl font-medium text-slate-700">
                  <FaBirthdayCake className="text-slate-950" />
                  Naissance :
                </p>

                <p className="text-[1.85rem] font-semibold text-slate-950">
                  {formatShortDateFR(adherent?.date_naissance)}
                </p>
              </div>

              <div className="grid grid-cols-[185px_minmax(0,1fr)] items-center border-b border-yellow-500/60 pb-2">
                <p className="flex items-center gap-3 text-xl font-medium text-slate-700">
                  <FaIdCard className="text-slate-950" />
                  Licence :
                </p>

                <p className="truncate rounded-xl bg-white px-3 py-1.5 text-[1.45rem] font-semibold text-slate-950 shadow-sm ring-1 ring-slate-200">
                  {adherent?.licence_number || "—"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MemberCardPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const location = useLocation();

  const cardRef = useRef(null);

  const [generating, setGenerating] = useState(false);

  const state = location.state || {};

  const adherent = state.adherent || null;
  const dossier = state.dossier || null;
  const settings = state.settings || {};

  const activeSeason =
    state.activeSeason ||
    settings?.saison_active ||
    adherent?.saison_active ||
    "";

  const canAccess = isReglementValidated(dossier);

  const filename = useMemo(() => {
    const prenom = cleanFileName(adherent?.prenom || "adherent");
    const nom = cleanFileName(adherent?.nom || "");
    const saison = cleanFileName(
      activeSeason || adherent?.saison_active || "saison",
    );

    return `carte-membre-${prenom}-${nom}-${saison}.pdf`;
  }, [activeSeason, adherent?.nom, adherent?.prenom, adherent?.saison_active]);

  const handleDownloadPdf = async () => {
    try {
      setGenerating(true);
      toast.info("Génération de la carte en cours...");

      const canvas = await buildMemberCardCanvasFromSafeHtml({
        adherent,
        dossier,
        settings,
        activeSeason,
      });

      const pdf = addCardCanvasToPdf(canvas);

      pdf.save(filename);

      toast.success("Carte membre téléchargée.");
    } catch (error) {
      console.error("Erreur téléchargement carte membre :", error);
      toast.error(
        error?.message || "Impossible de générer le PDF de la carte membre.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = async () => {
    const printWindow = window.open("", "_blank");

    if (!printWindow) {
      toast.error("La fenêtre d’impression a été bloquée par le navigateur.");
      return;
    }

    printWindow.document.write(`
    <html>
      <head>
        <title>Préparation impression</title>
      </head>
      <body style="font-family: Arial, sans-serif; padding: 24px;">
        Préparation de la carte membre...
      </body>
    </html>
  `);

    try {
      setGenerating(true);
      toast.info("Préparation de l’impression...");

      const canvas = await buildMemberCardCanvasFromSafeHtml({
        adherent,
        dossier,
        settings,
        activeSeason,
      });

      const pdf = addCardCanvasToPdf(canvas);

      pdf.autoPrint();

      const blob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(blob);

      printWindow.location.href = blobUrl;

      toast.success("Fenêtre d’impression ouverte.");
    } catch (error) {
      console.error("Erreur impression carte membre :", error);

      printWindow.close();

      toast.error(error?.message || "Impossible d’imprimer la carte membre.");
    } finally {
      setGenerating(false);
    }
  };

  if (!canAccess) {
    return (
      <section className="min-h-screen bg-slate-100 px-4 py-8 font-satoshi dark:bg-slate-950">
        <div className="mx-auto max-w-3xl rounded-[28px] border border-rose-200 bg-white p-6 shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-rose-600">
            Accès refusé
          </p>

          <h1 className="mt-2 text-2xl font-bold text-slate-950 dark:text-white">
            Règlement non validé
          </h1>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            La carte membre est disponible uniquement lorsque le règlement
            licence-cotisation est validé par le club.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
          >
            <FaArrowLeft />
            Retour
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-slate-100 px-4 py-8 font-satoshi text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex flex-col gap-3 rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-yellow-600">
              Carte membre
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
              {adherent.prenom} {adherent.nom}
            </h1>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Carte générée avec la photo, la licence, la saison active et le
              dossier d’inscription.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaArrowLeft />
              Retour
            </button>

            <button
              type="button"
              onClick={handlePrint}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
            >
              <FaPrint />
              Imprimer
            </button>

            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FaDownload />
              {generating ? "Génération..." : "Télécharger PDF"}
            </button>
          </div>
        </div>

        <div className="overflow-auto rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="mx-auto w-fit origin-top scale-[0.66] sm:scale-[0.78] md:scale-90 lg:scale-100">
            <MemberCard
              adherent={adherent}
              dossier={dossier}
              settings={settings}
              activeSeason={activeSeason}
              cardRef={cardRef}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
